import React, { useEffect, useMemo } from 'react';
import { FaEdit, FaTrash, FaBoxes } from 'react-icons/fa';
import notFoundImage from "../../../assets/fox-not-found.webp";
import { getImageCacheKey, getImageUrl } from "../../../lib/helpers";

// Icono por tipo de ubicación (almacén principal / nevera / armario / sub-almacén)
const LOC_ICON = { principal: '🏠', nevera: '🧊', armario: '🗄️', sub_almacen: '📦' };
const locIcon = (kind) => LOC_ICON[kind] || '📍';
const fmtQty = (n) => Number(n || 0).toLocaleString('es-ES', { maximumFractionDigits: 3 });

const LocBreakdown = ({ list = [], unitType }) => {
  if (!list.length) return null;
  return (
    <div className="flex flex-wrap gap-1 mt-1">
      {list.map((l) => {
        const neg = Number(l.qty) < 0;
        return (
        <span key={l.id} className={`inline-flex items-center gap-0.5 rounded px-1.5 py-0.5 text-[10px] font-medium ${neg ? 'bg-red-50 text-[#DC2626] ring-1 ring-red-200' : 'bg-gray-100 text-gray-600'}`} title={neg ? `${l.name} · en negativo, pendiente de reponer` : l.name}>
          <span aria-hidden>{neg ? '⚠️' : locIcon(l.kind)}</span>
          <span className="max-w-[90px] truncate">{l.name}</span>
          <span className={`font-semibold ${neg ? 'text-[#DC2626]' : 'text-gray-800'}`}>{fmtQty(l.qty)}{unitType === 'fixed' ? 'u' : 'g'}</span>
        </span>
        );
      })}
    </div>
  );
};

const ProductTable = ({ products, loading, onEdit, onDelete, onStock, onToggleSelect, selectedIds = [], token, venueID, refreshTrigger, readOnly = false, canEdit = true, searchQuery = "", onVisibleProductsChange, selectedCategory = "All", onCategoryChange, categories: externalCategories, stockByLoc = {} }) => {
  // Categories are owned by Products.jsx which fetches them with the correct
  // x-venue-id. No independent fetch here to avoid races.
  const allCategories = useMemo(() => externalCategories || [], [externalCategories]);

  const filteredProducts = useMemo(() => {
    const productsArray = Array.isArray(products) ? products : [];
    const searchActive = (searchQuery || "").trim().length > 0;

    if (searchActive) return productsArray;
    if (selectedCategory === 'All') return productsArray;
    if (selectedCategory === 'Sin categoría') {
      return productsArray.filter(p => {
        const n = p.category?.name;
        return !n || ['Uncategorized', 'uncategorized', 'Sin categoría'].includes(n);
      });
    }
    return productsArray.filter(
      p => p.category?.name === selectedCategory
    );
  }, [products, selectedCategory, searchQuery]);

  useEffect(() => {
    if (typeof onVisibleProductsChange !== 'function') return;
    const ids = (filteredProducts || []).map((p) => p.id);
    onVisibleProductsChange(ids);
  }, [filteredProducts, onVisibleProductsChange]);

  if (loading) {
    return (
      <div className="bg-white p-4 rounded-lg shadow border border-gray-200 flex-auto">
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-4"></div>
            <p className="text-gray-500">Cargando productos...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white p-4 rounded-lg shadow border border-gray-200 flex-auto">
      {/* Category filter buttons */}
      <div className="mb-6 bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex flex-wrap gap-3 items-center">
          <button
            onClick={() => onCategoryChange('All')}
            className={`px-5 py-2.5 rounded-lg border-2 font-medium text-sm transition-all duration-200 ${
              selectedCategory === 'All'
                ? 'bg-blue-600 text-white border-blue-600 shadow-md'
                : 'bg-white text-gray-700 border-gray-300 hover:border-blue-400 hover:text-blue-600'
            }`}
          >
            Todas
          </button>
          
          {/* Botón "Sin categoría" */}
          <button
            onClick={() => onCategoryChange('Sin categoría')}
            className={`px-5 py-2.5 rounded-lg border-2 font-medium text-sm transition-all duration-200 ${
              selectedCategory === 'Sin categoría'
                ? 'bg-orange-600 text-white border-orange-600 shadow-md'
                : 'bg-white text-gray-700 border-gray-300 hover:border-orange-400 hover:text-orange-600'
            }`}
          >
            Sin categoría
          </button>
          
          {allCategories
            .filter(cat => cat.name !== "Uncategorized" && cat.name !== "Sin categoría" && cat.name !== "uncategorized")
            .map(cat => (
            <button
              key={cat.id}
              onClick={() => onCategoryChange(cat.name)}
              className={`px-5 py-2.5 rounded-lg border-2 font-medium text-sm transition-all duration-200 ${
                selectedCategory === cat.name
                  ? 'bg-blue-600 text-white border-blue-600 shadow-md'
                  : 'bg-white text-gray-700 border-gray-300 hover:border-blue-400 hover:text-blue-600'
              }`}
            >
              {cat.name}
            </button>
          ))}
        </div>
      </div>

      {/* Estado vacío: mantener filtros visibles */}
      {(!filteredProducts || filteredProducts.length === 0) && (
        <div className="bg-white rounded-lg shadow border border-gray-200 p-8 text-center text-gray-500 mb-6">
          {(searchQuery || "").trim()
            ? 'No se encontraron productos para esa búsqueda'
            : selectedCategory === 'All'
            ? 'No hay productos disponibles'
            : selectedCategory === 'Sin categoría'
            ? 'No se encontraron productos sin categoría asignada'
            : `No se encontraron productos en la categoría "${selectedCategory}"`}
        </div>
      )}

      {/* Listado responsive (móvil + tablet) */}
      <div className="block lg:hidden space-y-3">
        {(filteredProducts || []).map((product) => {
          const imageUrl = getImageUrl(product.image_path, notFoundImage);

          return (
            <div
              key={product.id}
              className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 hover:shadow-md hover:border-blue-300 transition-all duration-200"
            >
              <div className="mb-3 flex justify-end">
                {!readOnly ? (
                  <input
                    type="checkbox"
                    checked={selectedIds.some((id) => String(id) === String(product.id))}
                    onChange={() => onToggleSelect(product.id)}
                    className="h-4 w-4 accent-blue-600"
                  />
                ) : null}
              </div>
              <div className="flex items-center gap-3 mb-4">
                <img
                  src={imageUrl}
                  alt={product.name}
                  className="w-[5.6rem] h-[5.6rem] rounded-xl object-cover border-2 border-gray-300 flex-shrink-0"
                  onError={(e) => {
                    e.target.src = notFoundImage;
                  }}
                />
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-gray-900 truncate">{product.name || '-'}</div>
                  <div className="text-xs text-gray-500 truncate">ID: {product.id?.toString().slice(0, 8) || '-'}</div>
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-3 mb-4 grid grid-cols-2 gap-3 text-sm">
                <div className="flex flex-col">
                  <span className="text-xs text-gray-500 mb-0.5">Categoría</span>
                  <span className="px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800 w-fit">
                    {(() => {
                      const n = product.category?.name;
                      if (!n) return 'Sin categoría';
                      return ['Uncategorized', 'uncategorized'].includes(n) ? 'Sin categoría' : n;
                    })()}
                  </span>
                </div>
                <div className="flex flex-col">
                  <span className="text-xs text-gray-500 mb-0.5">Precio</span>
                  <span className="font-semibold text-gray-900">€{product.price || '0.00'}</span>
                </div>
                {!readOnly ? (
                  <div className="flex flex-col">
                    <span className="text-xs text-gray-500 mb-0.5">Stock</span>
                    <span className={`px-2 py-1 text-xs font-semibold rounded-full w-fit ${
                      (product.stock_quantity || 0) > 10
                        ? 'bg-green-100 text-green-800'
                        : (product.stock_quantity || 0) > 0
                        ? 'bg-yellow-100 text-yellow-800'
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {product.stock_quantity || 0}
                    </span>
                    <LocBreakdown list={stockByLoc[product.id]} unitType={product.unit_type} />
                  </div>
                ) : null}
                <div className="flex flex-col">
                  <span className="text-xs text-gray-500 mb-0.5">Tipo</span>
                  <span className="px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-800 w-fit">
                    {product.unit_type === 'fixed' ? 'Unidad' : 'Peso'}
                  </span>
                </div>
                <div className="flex flex-col col-span-2">
                  <span className="text-xs text-gray-500 mb-0.5">Rentabilidad</span>
                  {product.cost_ex_vat == null ? (
                    <span className="px-2 py-1 text-xs font-semibold rounded-full bg-amber-100 text-amber-800 w-fit">
                      Sin coste informado
                    </span>
                  ) : (
                    <span className="px-2 py-1 text-xs font-semibold rounded-full bg-emerald-100 text-emerald-800 w-fit">
                      Coste {Number(product.cost_ex_vat).toFixed(2)} EUR
                    </span>
                  )}
                </div>
              </div>

              {(!readOnly && canEdit) ? (
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={() => onEdit(product.id)}
                    className="flex items-center justify-center gap-2 px-3 py-2.5 bg-lime-600 hover:bg-lime-700 text-white rounded-lg text-sm font-semibold shadow-sm hover:shadow transition-all active:scale-95"
                    title="Editar producto"
                  >
                    <FaEdit />
                    <span>Editar</span>
                  </button>
                  <button
                    onClick={() => onDelete(product.id)}
                    className="flex items-center justify-center gap-2 px-3 py-2.5 bg-red-700 hover:bg-red-800 text-white rounded-lg text-sm font-semibold shadow-sm hover:shadow transition-all active:scale-95"
                    title="Eliminar producto"
                  >
                    <FaTrash />
                    <span>Eliminar</span>
                  </button>
                </div>
              ) : null}
              {(!readOnly && canEdit && onStock) ? (
                <button
                  onClick={() => onStock(product.id)}
                  className="mt-2 w-full flex items-center justify-center gap-2 px-3 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-semibold shadow-sm hover:shadow transition-all active:scale-95"
                  title="Entrar / ajustar stock e historial"
                >
                  <FaBoxes />
                  <span>Actualizar stock</span>
                </button>
              ) : null}
            </div>
          );
        })}
      </div>

      <div className="hidden lg:block overflow-x-auto">
        <table className="w-full text-left text-gray-700">
          <thead className="text-xs text-gray-700 uppercase bg-gray-50">
            <tr>
              <th scope="col" className="py-3 px-4 w-12">
                <span className="sr-only">Seleccionar</span>
              </th>
              <th scope="col" className="py-3 px-4">
                Producto
              </th>
              <th scope="col" className="py-3 px-4">
                Categoría
              </th>
              <th scope="col" className="py-3 px-4">
                Precio
              </th>
              <th scope="col" className="py-3 px-4">
                Stock
              </th>
              <th scope="col" className="py-3 px-4">
                Tipo
              </th>
              <th scope="col" className="py-3 px-4">
                Rentabilidad
              </th>
              <th scope="col" className="py-3 px-4 text-right">
                Acciones
              </th>
            </tr>
          </thead>
          <tbody>
            {(filteredProducts || []).map((product) => {
              const imageUrl = getImageUrl(product.image_path, notFoundImage, {
                cacheKey: getImageCacheKey('product', product.id),
              });

              return (
                <tr key={product.id} className="border-b odd:bg-white even:bg-gray-50 hover:bg-gray-100">
                  <td className="py-3 px-4 align-middle">
                    <input
                      type="checkbox"
                      checked={selectedIds.some((id) => String(id) === String(product.id))}
                      onChange={() => onToggleSelect(product.id)}
                      className="h-4 w-4 accent-blue-600"
                    />
                  </td>
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-3">
                      <img
                        src={imageUrl}
                        alt={product.name}
                        className="h-[2.1rem] w-[2.1rem] rounded object-cover border border-gray-300"
                        onError={(e) => {
                          e.target.src = notFoundImage;
                        }}
                      />
                      <div>
                        <div className="font-medium text-gray-900">{product.name || '-'}</div>
                        <div className="text-xs text-gray-500">ID: {product.id?.toString().slice(0, 8) || '-'}</div>
                      </div>
                    </div>
                  </td>
                  <td className="py-3 px-4">
                    <span className="px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                      {(() => {
                        const n = product.category?.name;
                        if (!n) return 'Sin categoría';
                        return ['Uncategorized', 'uncategorized'].includes(n) ? 'Sin categoría' : n;
                      })()}
                    </span>
                  </td>
                  <td className="py-3 px-4">
                    <span className="font-semibold text-gray-900">
                      €{product.price || '0.00'}
                    </span>
                  </td>
                  <td className="py-3 px-4">
                    <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                      (product.stock_quantity || 0) > 10 
                        ? 'bg-green-100 text-green-800' 
                        : (product.stock_quantity || 0) > 0 
                        ? 'bg-yellow-100 text-yellow-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {product.stock_quantity || 0}
                    </span>
                    <LocBreakdown list={stockByLoc[product.id]} unitType={product.unit_type} />
                  </td>
                  <td className="py-3 px-4">
                    <span className="px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-800">
                      {product.unit_type === 'fixed' ? 'Unidad' : 'Peso'}
                    </span>
                  </td>
                  <td className="py-3 px-4">
                    {product.cost_ex_vat == null ? (
                      <span className="px-2 py-1 text-xs font-semibold rounded-full bg-amber-100 text-amber-800">
                        Sin coste
                      </span>
                    ) : (
                      <div className="flex flex-col">
                        <span className="text-sm font-medium text-gray-900">€{Number(product.cost_ex_vat).toFixed(2)}</span>
                        <span className="text-xs text-gray-500">{product.supplier_name || 'Sin proveedor'}</span>
                      </div>
                    )}
                  </td>
                  <td className="py-3 px-4 text-right space-x-1">
                    {(!readOnly && canEdit) && (
                      <>
                        {onStock && (
                          <button
                            onClick={() => onStock(product.id)}
                            className="p-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded"
                            title="Entrar / ajustar stock e historial"
                          >
                            <FaBoxes />
                          </button>
                        )}
                        <button
                          onClick={() => onEdit(product.id)}
                          className="p-2 bg-lime-600 hover:bg-lime-800 text-white rounded"
                          title="Editar producto"
                        >
                          <FaEdit />
                        </button>
                        <button
                          onClick={() => onDelete(product.id)}
                          className="p-2 bg-red-700 hover:bg-red-800 text-white rounded"
                          title="Eliminar producto"
                        >
                          <FaTrash />
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ProductTable;
